// ASYNC REQUEST HELPERS

import Qs from 'qs';
import axios from 'axios';
import {formatActionUrl} from '@/js/global/helpers/url';

/**
 * The object key name for submitting a CSRF value in a POST request.
 *
 * @type {string}
 */
export const CSRF_TOKEN_KEY_NAME = 'CRAFT_CSRF_TOKEN';

/**
 * Include these headers in your POST request when you want to accept JSON as the response format.
 *
 * @type {{'X-Requested-With': string, Accept: string}}
 */
export const ACCEPTS_JSON_HEADERS = {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'};

const randomGenerator = function (specialChars = false, length = 1, avoidChars = '') {
    let _pattern = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    _pattern += specialChars === true ? '(){}[]+-*/=' : '';
    if (avoidChars && avoidChars.length) {
        for (let char of avoidChars) {
            _pattern = _pattern.replace(char, '');
        }
    }
    let _random = '';
    for (let element of new Array(parseInt(length))) {
        _random += _pattern.charAt(Math.floor(Math.random() * _pattern.length));
    }
    return _random;
};

/**
 * Get a CSRF token value to make a POST request to a Yii controller action with JavaScript.
 *
 * @param forceReset {boolean}
 * @return {Promise<string|*>}
 */
export async function getCsrfToken(forceReset = false) {
   // reuse the token already stored on the global window
   if (! forceReset && typeof window.csrfTokenValue === 'string' && window.csrfTokenValue !== '') {
      return window.csrfTokenValue;
   }

   // get user's session data from Craft
   const response = await makeGetRequest(`users/session-info${forceReset ? `?${randomGenerator(false, 8)}` : ''}`, {}, true);

   // set the token value on the global window for reuse
   window.csrfTokenValue = response.data.csrfTokenValue;

   return window.csrfTokenValue;
};

/**
 * Make a GET request to any Yii controller action URL.
 *
 * @param action {string}
 * @param config {object}
 * @param acceptsJson {boolean}
 * @return {Promise<AxiosResponse<any>|*>}
 */
export async function makeGetRequest(action, config = {}, acceptsJson = false) {

   // ensure that we have the right headers for accepting JSON in the response
   if (acceptsJson) {
      if (! config.headers) {
         config.headers = {};
      }
      config.headers = Object.assign(config.headers, ACCEPTS_JSON_HEADERS);
   }

   try {
      return await axios.get(formatActionUrl(action), config);
   }
   catch (error) {
      if(window.Sentry) {
         Sentry.captureException(error);
      }
      console.error(error?.response?.data);
      return error;
   }
}

/**
 * Make a POST request to any Yii controller action URL.
 *
 * @param action {string}
 * @param data {object}
 * @param config {object}
 * @param rerun {boolean}
 * @return {AxiosResponse<any>|*>}
 */
export async function makePostRequest(action, data = {}, config = {}, rerun = true) {

   // ensure the token exists in the post data
   if (! (CSRF_TOKEN_KEY_NAME in data) || ! data[CSRF_TOKEN_KEY_NAME]) {
      data[CSRF_TOKEN_KEY_NAME] = await getCsrfToken();
   }

   // ensure that we have the right headers for accepting JSON in the response
   if (! ('headers' in config)) {
      config.headers = {};
   }
   config.headers = Object.assign(config.headers, ACCEPTS_JSON_HEADERS);

   // post to the controller action
   try {
      return await axios.post(formatActionUrl(action), Qs.stringify(data), config);
   }
   catch (error) {
      // refresh the CSRF token and run the request again
      if (rerun) {
         await new Promise(r => setTimeout(r, 1000));
         data[CSRF_TOKEN_KEY_NAME] = await getCsrfToken(true);

         await new Promise(r => setTimeout(r, 500));
         return await makePostRequest(action, data, {}, false);
      }

      if(window.Sentry) {
         Sentry.captureException(error);
      }
      console.log(data, error?.response?.data);
      return error;
   }
}

/**
 * Cause a delay in action after a certain time passes of user inactivity.
 *
 * @param fn The function that runs after the 'wait' time finishes.
 * @param wait The wait time (in milliseconds).
 * @return {(function(...[*]=): void)|*}
 * @see https://blog.logrocket.com/debounce-throttle-vue/
 */
export function debounce(fn, wait){
   let timer;

   return function(...args) {
      // clear any pre-existing timer
      if (timer) {
         clearTimeout(timer);
      }

      // get the current context
      const context = this;

      // call the function if time expires
      timer = setTimeout(() => {
         fn.apply(context, args);
      }, wait);
   }
}

/**
 * Call a given function at intervals of a given time while the user is carrying out an event.
 *
 * @param fn The function that runs every interval of 'wait' time.
 * @param wait The wait time (in milliseconds).
 * @return {(function(...[*]=): void)|*}
 * @see https://blog.logrocket.com/debounce-throttle-vue/
 */
export function throttle(fn, wait){
   let throttled = false;

   return function(...args) {
      if(! throttled) {
         fn.apply(this,args);
         throttled = true;
         setTimeout(() => {
            throttled = false;
         }, wait);
      }
   }
}